
import { mapStores } from 'pinia';
import { defineComponent, PropType } from 'vue';
import { NSelect, NSpin } from 'naive-ui';

import { Station } from '~/api/models/station.model';
import StationEditionModal from '~/components/modals/station-edition-modal.vue';
import { useStationStore } from '~/store/station.store';
import StationListItem from './station-list-item.vue';
import componentBase from '~/components/component-base';
import { cloneDeep, get, isNil } from 'lodash';

export default defineComponent({

    extends: componentBase,

    components: {
        StationListItem,
        NSelect,
        NSpin,
        StationEditionModal,
    },

    props: {
        stations: { type: Array as PropType<Station[]>, required: true },
        selected: { type: Array as PropType<Station[]>, default: () => []},
        multiple: { type: Boolean, default: false },
    },

    data() {
        return {
            searchTerms: '',
            statusFilter: null,
            selectedIds: null,
            statusFilterOptions: [
                { label: this.$t('global.all'), value: null},
                { label: this.$t('station_list.filters.online'), value: 'online'},
                { label: this.$t('station_list.filters.offline'), value: 'offline'},
            ],
        }
    },

    mounted(): void {
        this.selectedIds = this.selected && this.selected.map((station: Station) => station.id) || [];
    },

    computed: {
        ...mapStores(useStationStore),

        filteredStations(): Station[] {

            if(!this.searchTerms && !this.statusFilter) {
                return this.stations;
            }

            const terms: string = this.searchTerms.toLowerCase();

            return this.stations
                .filter((station: Station) => {
                    return this.statusFilter === null || station.status === this.statusFilter;
                })
                .filter((station: Station) => {
                    const label = station.metadata['elichens/public/label'];
                    return station.name.toLowerCase().includes(terms) || label?.toLowerCase().includes(terms);
                })
        }
    },

    methods: {
        selectStation(station: Station): void {

            if(!station || !station.id) {
                return;
            }
            
            let query = [];

            // if select is multiple and station is already selected, remove it from selected list.
            if(this.multiple && this.isSelected(station)) {
                query = this.selected
                    .filter((s: Station) => s.id !== station.id)
                    .map(((s: Station) => s.id));
            } else if(this.multiple) {
                const selected = cloneDeep(this.selected);
                selected.push(station);
                query = selected.map(((s: Station) => s.id));
            } else {
                query = [station.id];
            }

            this.selectedIds = query;

            this.$router.replace({ query: { ...this.$route.query, station: query } });
        },

        isSelected(station: Station) {
            return this.selected && this.selected.findIndex((s: Station) => s.id === station.id) !== -1;
        },

        async addStation(): Promise<void> {
            return this.$prompt(this.$t('station_list.station_added_title'), { placeholder: this.$t('add_station.enter_pairing_token')})
                .then(async (result: string) => {

                    if (!result) {
                        return Promise.resolve();
                    }

                    return this.stationStore.pairStation(result);
                })
                .then((station?: Station) => {
                    if (!station) {
                        return;
                    }

                    this.success(this.$t('station_list.station_added_success'));
                    return station;
                })
                .then((station: Station) => {

                    if(!station) {
                        return;
                    }

                    const required = [
                        { path: 'location.lat', label: this.$t('global.latitude')}, 
                        { path: 'location.lon', label: this.$t('global.longitude')},
                        { path: 'metadata.elichens/public/label', label: this.$t('global.name')},
                        { path: 'metadata.elichens/public/locality', label: this.$t('global.city')},
                        { path: 'metadata.elichens/public/location', label: this.$t('global.location')},
                    ];

                    const missingFields = required.filter(r => isNil(get(station, r.path)));
                    if(missingFields.length === 0) {
                        return;
                    }

                    this.notification(
                        'info', 
                        this.$t('station_list.add.station_not_configurated'),
                        this.$t('station_list.add.missing informations', {missings: missingFields.map(m => m.label).join(this.$t('global.separator'))}),
                        { 
                            label: this.$t('station_list.edit.title'),
                            callback: () => this.editStation(station),
                        }
                    )
                })
                .catch((e) => {
                    this.showUnhandledError(this.$t('station_list.station_added_failure'), e);
                });
        },

        editStation(station: Station): void {
            if(!this.$refs.stationEditionModal) {
                return;
            }

            //@ts-ignore
            this.$refs.stationEditionModal?.editStation(station);
        },
    },

    watch: {
        selected() {
            this.selectedIds = this.selected && this.selected.map((station: Station) => station.id) || [];
        }
    }
});

